// Copyright (c) Ping. All rights reserved.
//
// E2EE media key derivation + rotation for LiveKit calls.
//
// The media key is derived from the per-call MLS group (calls.md §3–4) via the
// SDK exporter (RFC 9420 §8.5), bound to the call by `call_id`:
//
//   media_key = exportConversationSecret(callGroup, "ping/call-media", call_id, 32)
//
// Only the call's participants are members of that group, so ONLY they can
// derive the key (per-call PCS). All members at the same epoch derive
// byte-identical keys with no extra round-trip. The key is fed to LiveKit's
// ExternalE2EEKeyProvider before publish/subscribe, so the SFU only ever sees
// ciphertext frames.
//
// ROTATION: every join/leave Commit on the call group advances its MLS epoch,
// changing the exported secret. We re-derive on the SDK's
// `onConversationUpdated(id, epoch)` callback (fires after every Commit) and
// swap the key atomically — immediate PCS when a participant leaves the call.
//
// Note: callers pass the CALL GROUP id (not the host conversation) as
// `conversationIdHex` below — the function is id-agnostic.

import type { ExternalE2EEKeyProvider } from 'livekit-client';

import { hexToBytes } from '@ping/ui';

export const MEDIA_KEY_LABEL = 'ping/call-media';
export const MEDIA_KEY_BYTES = 32;

/** Minimal SDK surface the media-key layer needs. */
export interface MediaKeyClient {
  exportConversationSecret(
    conv: Uint8Array,
    label: string,
    context: Uint8Array,
    length: number,
  ): Promise<Uint8Array>;
  onConversationUpdated(
    handler: (id: Uint8Array, epoch: number, sender?: Uint8Array) => void,
  ): () => void;
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * CROSS-PLATFORM CONTRACT (calls-drift §7): both web and iOS use the raw-bytes
 * (HKDF) path. Web: `keyProvider.setKey(ArrayBuffer)` →
 * `createKeyMaterialFromBuffer` → HKDF-SHA256(salt="LKFrameEncryptionKey",
 * info=zeros[128]). iOS: `BaseKeyProvider.setKeyData(Data)` → native
 * `setSharedKey` with `keyDerivationAlgorithm=.hkdf` → same HKDF parameters.
 * Both produce the identical AES-128-GCM key from the same raw 32-byte secret.
 *
 * `toKeyString` is kept for documentation; it is no longer called in the hot
 * path — the ArrayBuffer slice is passed directly to `keyProvider.setKey`.
 */
export function toKeyBytes(u8: Uint8Array): ArrayBuffer {
  // Ensure a standalone 32-byte buffer regardless of the Uint8Array's byteOffset.
  return (u8.buffer as ArrayBuffer).slice(u8.byteOffset, u8.byteOffset + u8.byteLength);
}

/** @deprecated Use `toKeyBytes` (ArrayBuffer path, HKDF) instead. */
export function toKeyString(u8: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < u8.length; i += 1) bin += String.fromCharCode(u8[i]!);
  return btoa(bin);
}

/**
 * Derive the current media key for `callIdHex` from `conversationIdHex`. Throws
 * if the conversation isn't known to the SDK yet (caller should retry).
 */
export async function deriveCallMediaKey(
  client: MediaKeyClient,
  conversationIdHex: string,
  callIdHex: string,
): Promise<Uint8Array> {
  const convId = hexToBytes(conversationIdHex);
  const callId = hexToBytes(callIdHex);
  return client.exportConversationSecret(convId, MEDIA_KEY_LABEL, callId, MEDIA_KEY_BYTES);
}

/**
 * Subscribe to host-conversation Commits and re-feed the key provider whenever
 * the epoch advances (membership change → new exported secret). Returns an
 * unsubscribe. Re-derivation is best-effort; a transient export failure leaves
 * the previous key in place (LiveKit keeps decrypting the current epoch).
 */
export function installMediaKeyRotation(args: {
  client: MediaKeyClient;
  keyProvider: ExternalE2EEKeyProvider;
  conversationIdHex: string;
  callIdHex: string;
  onError?: (err: unknown) => void;
  /** Diagnostic only: the new call-group epoch driving this rotation. */
  onEpoch?: (epoch: number) => void;
}): () => void {
  const { client, keyProvider, conversationIdHex, callIdHex, onError, onEpoch } = args;
  const convId = hexToBytes(conversationIdHex);
  return client.onConversationUpdated((id, epoch) => {
    if (!sameBytes(id, convId)) return;
    onEpoch?.(epoch);
    void deriveCallMediaKey(client, conversationIdHex, callIdHex)
      .then((key) => keyProvider.setKey(toKeyBytes(key)))
      .catch((err) => onError?.(err));
  });
}
